/**
 * Client-side lookup for well-known Norwegian public holiday names, keyed by date.
 *
 * Stopgap only: the reception holiday endpoint has no name/label field yet (BE is adding one).
 * Until then, this recognises official Norwegian public holidays purely for display — arbitrary
 * company-added closures that don't match a known holiday are shown with no name, same as before.
 * Once BE ships a real field, prefer that value and drop this lookup.
 */

/** Returns the Gregorian date of Easter Sunday for the given year (Meeus/Jones/Butcher algorithm). */
function getEasterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = March, 4 = April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const FIXED_HOLIDAYS: { month: number; day: number; nb: string; en: string }[] = [
  { month: 1, day: 1, nb: "Første nyttårsdag", en: "New Year's Day" },
  { month: 5, day: 1, nb: "Arbeidernes dag", en: "Labour Day" },
  { month: 5, day: 17, nb: "Grunnlovsdag", en: "Constitution Day" },
  { month: 12, day: 24, nb: "Julaften", en: "Christmas Eve" },
  { month: 12, day: 25, nb: "Første juledag", en: "Christmas Day" },
  { month: 12, day: 26, nb: "Andre juledag", en: "Boxing Day" },
  { month: 12, day: 31, nb: "Nyttårsaften", en: "New Year's Eve" },
];

/** Offsets in days from Easter Sunday; negative = before, positive = after. */
const EASTER_RELATIVE_HOLIDAYS: { offset: number; nb: string; en: string }[] = [
  { offset: -3, nb: "Skjærtorsdag", en: "Maundy Thursday" },
  { offset: -2, nb: "Langfredag", en: "Good Friday" },
  { offset: 0, nb: "Første påskedag", en: "Easter Sunday" },
  { offset: 1, nb: "Andre påskedag", en: "Easter Monday" },
  { offset: 39, nb: "Kristi himmelfartsdag", en: "Ascension Day" },
  { offset: 49, nb: "Første pinsedag", en: "Whit Sunday" },
  { offset: 50, nb: "Andre pinsedag", en: "Whit Monday" },
];

function buildHolidayMapForYear(year: number): Map<string, { nb: string; en: string }> {
  const map = new Map<string, { nb: string; en: string }>();

  FIXED_HOLIDAYS.forEach(({ month, day, nb, en }) => {
    map.set(toDateKey(new Date(year, month - 1, day)), { nb, en });
  });

  const easterSunday = getEasterSunday(year);
  EASTER_RELATIVE_HOLIDAYS.forEach(({ offset, nb, en }) => {
    map.set(toDateKey(addDays(easterSunday, offset)), { nb, en });
  });

  return map;
}

/**
 * Looks up the known Norwegian public holiday name for a "YYYY-MM-DD" date, in the given
 * language. Returns undefined for dates that aren't a recognised public holiday.
 */
export function getKnownHolidayName(dateStr: string, language: "nb" | "en" = "nb"): string | undefined {
  const year = Number(dateStr.slice(0, 4));
  if (!Number.isFinite(year)) return undefined;
  const entry = buildHolidayMapForYear(year).get(dateStr);
  return entry?.[language];
}
